import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { shouldShowQuestionnaire, postQuestionnaireResponses, postSkipQuestionnaire, fetchMe, setPreferredLanguage } from '../api';
import { getStoredLanguage, useI18n } from '../i18n';

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [woodStyle, setWoodStyle] = useState('oak');
  const [questionnaireState, setQuestionnaireState] = useState('hidden');
  const [startTime] = useState(Date.now());
  const pendingAuthLanguageRef = useRef(null);
  const navigate = useNavigate();
  const { syncLanguage } = useI18n();

  const applyUserLanguage = useCallback((nextUser, options = {}) => {
    if (!nextUser) return;
    const pendingLanguage = pendingAuthLanguageRef.current;
    const storedLanguage = getStoredLanguage();

    const preserveLanguage =
      typeof options.preserveLocalLanguage === 'string'
        ? options.preserveLocalLanguage
        : pendingLanguage || storedLanguage;

    const languageOverride =
      typeof preserveLanguage === 'string'
        ? preserveLanguage
        : null;
    const resolvedUser = languageOverride
      ? {
          ...nextUser,
          preferredLanguage: languageOverride
        }
      : nextUser;

    setUser(resolvedUser);

    if (languageOverride) {
      syncLanguage(languageOverride);
    } else if (resolvedUser.preferredLanguage || resolvedUser.preferred_language) {
      syncLanguage(
        resolvedUser.preferredLanguage ||
        resolvedUser.preferred_language
      );
    }
  }, [syncLanguage]);

  const getSessionTime = useCallback(() => {
    return Math.floor((Date.now() - startTime) / 1000);
  }, [startTime]);

  useEffect(() => {
    const styles = ['oak', 'bamboo', 'parquet'];
    setWoodStyle(styles[Math.floor(Math.random() * styles.length)]);
  }, []);

  const checkQuestionnaire = useCallback(async () => {
    if (!user) return;
    try {
      const res = await shouldShowQuestionnaire();
      setQuestionnaireState(res.data.state);
    } catch {
      setQuestionnaireState('hidden');
    }
  }, [user]);

  useEffect(() => {
    let alive = true;
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decoded.exp && decoded.exp < currentTime) {
           logout();
           return;
        }
        localStorage.setItem('token', token);
        const authLanguageOverride = pendingAuthLanguageRef.current;
        applyUserLanguage(decoded, { preserveLocalLanguage: authLanguageOverride });
        fetchMe()
          .then(async (res) => {
            if (!alive) return;
            const me = res.data?.user || res.data;
            if (me) {
              applyUserLanguage(me, { preserveLocalLanguage: authLanguageOverride });
              if (authLanguageOverride) {
                try {
                  const languageRes = await setPreferredLanguage(authLanguageOverride);
                  if (!alive) return;
                  const syncedUser = languageRes.data?.user || languageRes.data;
                  if (syncedUser) {
                    applyUserLanguage(syncedUser, { preserveLocalLanguage: authLanguageOverride });
                  }
                  pendingAuthLanguageRef.current = null;
                } catch (languageErr) {
                  console.warn('[i18n] Could not persist post-auth language preference; keeping local language.', languageErr);
                }
              }
            }
          })
          .catch((err) => {
            console.error('Error syncing authenticated user:', err);
          });
      } catch (err) {
        logout();
      }
    } else {
      localStorage.removeItem('token');
      pendingAuthLanguageRef.current = null;
      setUser(null);
    }
    return () => { alive = false; };
  }, [applyUserLanguage, token]);

  useEffect(() => {
    checkQuestionnaire();
  }, [checkQuestionnaire]);

  const handleQuestionnaireComplete = async (answers) => {
    if (!user) return;
    try {
      await postQuestionnaireResponses({ answers });
      setQuestionnaireState('completed');
      navigate('/');
    } catch (err) {
      console.error('Error submitting questionnaire:', err);
    }
  };

  const handleSkip = async () => {
    try {
      await postSkipQuestionnaire();
      setQuestionnaireState('skipped');
      navigate('/');
    } catch (err) {
      console.error('Error skipping questionnaire:', err);
    }
  };

  const login = (newToken) => {
    const storedLanguage = getStoredLanguage();
    if (storedLanguage) {
      pendingAuthLanguageRef.current = storedLanguage;
      syncLanguage(storedLanguage);
    }
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setQuestionnaireState('hidden'); // Reset on new login
  };

  const logout = () => {
    try {
      window.dispatchEvent(new CustomEvent('q-agent-cancel', { detail: { reason: 'logout' } }));
    } catch (_) {}
    setToken(null);
  };

  const refreshUser = async (options = {}) => {
    try {
      const res = await fetchMe();
      // CRITICAL FIX: El endpoint /me devuelve { user: ... }.
      // Debemos extraer 'user' para mantener la estructura plana compatible con el token.
      if (res.data && res.data.user) {
        applyUserLanguage(res.data.user, options);
      } else if (res.data) {
        // Fallback por robustez
        applyUserLanguage(res.data, options);
      }
    } catch (err) {
      console.error('Error refreshing user:', err);
    }
  };

  const updateUserAvatar = (newAvatar) => {
    setUser((prev) => (prev ? { ...prev, avatar: newAvatar } : null));
  };

  const updateSphereMode = (mode) => {
    setUser((prev) => (prev ? { ...prev, sphereMode: mode } : null));
  };

  const value = {
    token,
    user,
    login,
    logout,
    refreshUser,
    updateUserAvatar,
    updateSphereMode,
    woodStyle,
    questionnaireState,
    handleQuestionnaireComplete,
    handleSkip,
    getSessionTime
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;
